import { useState } from "react";
import { CheckCircle2, Clock, AlertTriangle, Circle, Loader } from "lucide-react";
import { inr, fmtDate } from "@/lib/api";
import { Skeleton } from "@/components/common";

const META = {
  Paid: { cls: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", icon: CheckCircle2 },
  "Due Soon": { cls: "bg-amber-50 text-amber-700 ring-amber-600/20", icon: Clock },
  Overdue: { cls: "bg-red-50 text-red-700 ring-red-600/20", icon: AlertTriangle },
  Upcoming: { cls: "bg-slate-100 text-slate-600 ring-slate-500/20", icon: Circle },
  Pending: { cls: "bg-blue-50 text-blue-700 ring-blue-600/20", icon: Loader },
};

function Badge({ status }) {
  const m = META[status] || META.Upcoming;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${m.cls}`}>
      <Icon size={12} /> {status}
    </span>
  );
}

export function EMISchedule({ schedule }) {
  const [filter, setFilter] = useState("All");
  if (!schedule) return <Skeleton className="h-64 w-full" />;

  const counts = schedule.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});
  const legend = ["Paid", "Due Soon", "Overdue", "Upcoming", "Pending"];
  const rows = filter === "All" ? schedule : schedule.filter((r) => r.status === filter);

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-5">
        <div>
          <h3 className="font-display font-bold text-[#0B132B]">EMI Repayment Schedule</h3>
          <p className="text-xs text-slate-400">Month-by-month repayment schedule</p>
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-slate-500">
          <span><b className="text-emerald-600">{counts.Paid || 0}</b> Paid</span>
          <span><b className="text-slate-700">{(counts.Upcoming || 0) + (counts["Due Soon"] || 0)}</b> Upcoming</span>
          {counts.Overdue ? <span><b className="text-red-600">{counts.Overdue}</b> Overdue</span> : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-5 pt-4">
        {["All", "Paid", "Upcoming", "Due Soon", "Overdue"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`emi-filter-${s.replace(/\s+/g, "-").toLowerCase()}`}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold ${filter === s ? "bg-[#0B132B] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{s}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 px-5 pt-3">
        {legend.map((s) => <Badge key={s} status={s} />)}
      </div>

      {/* Desktop table */}
      <div className="mt-4 hidden max-h-[520px] overflow-y-auto scroll-thin md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              {["#", "Due Date", "EMI Amount", "Outstanding Balance", "Status", "Paid On"].map((h) => (
                <th key={h} className="whitespace-nowrap px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.emi_no} className="hover:bg-slate-50" data-testid={`emi-row-${r.emi_no}`}>
                <td className="px-4 py-3 font-semibold text-[#0B132B]">{r.emi_no}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDate(r.due_date)}</td>
                <td className="px-4 py-3 font-semibold text-[#0B132B]">{inr(r.emi)}</td>
                <td className="px-4 py-3 text-slate-600">{inr(r.remaining_principal)}</td>
                <td className="px-4 py-3"><Badge status={r.status} /></td>
                <td className="px-4 py-3 text-xs text-slate-400">{r.paid_date ? fmtDate(r.paid_date) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="mt-4 max-h-[560px] space-y-3 overflow-y-auto scroll-thin p-4 md:hidden">
        {schedule.map((r) => (
          <div key={r.emi_no} className="rounded-lg border border-slate-200 p-4" data-testid={`emi-card-${r.emi_no}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="grid h-7 w-7 place-items-center rounded-md bg-slate-100 text-xs font-bold text-[#0B132B]">{r.emi_no}</span>
                <span className="text-sm font-semibold text-[#0B132B]">{fmtDate(r.due_date)}</span>
              </div>
              <Badge status={r.status} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div><p className="text-slate-400">EMI Amount</p><p className="font-semibold text-[#0B132B]">{inr(r.emi)}</p></div>
              <div><p className="text-slate-400">Outstanding Balance</p><p className="font-medium text-[#0B132B]">{inr(r.remaining_principal)}</p></div>
            </div>
            {r.paid_date && <p className="mt-2 text-xs text-emerald-600">Paid on {fmtDate(r.paid_date)}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
